import { ROUTES } from "../constants";
import { apiRequest } from "./api.client";

export { ApiError as NotesApiError } from "./api.errors";

// ── Types ──────────────────────────────────────────────────────────────────────

export type NoteSection = "subjective" | "objective" | "assessment" | "plan" | "general";

export interface VisitNoteEntryResponse {
  entry_id: string;
  visit_id: string;
  section?: NoteSection | null;
  author_user_id: string;
  author_doctor_id: string;
  author_name: string;
  author_role: string;
  body: string;
  status: "active" | "retracted";
  created_at: string;
  updated_at: string;
  current_version_no: number;
  // Computed server-side for the calling user on every request — the edit
  // button should trust this, not a client-side re-derivation of the policy.
  can_edit: boolean;
}

export interface VisitNoteEntryCreate {
  body: string;
  section?: NoteSection;
}

export interface VisitNoteEntryUpdate {
  body: string;
  edit_reason?: string;
}

export interface VisitNoteEntryVersionResponse {
  version_id: string;
  entry_id: string;
  version_no: number;
  body: string;
  edited_by_user_id: string;
  edited_by_doctor_id: string;
  edited_by_name: string;
  edited_by_role: string;
  edited_at: string;
  edit_reason?: string | null;
}

// A VisitNoteEntryResponse tagged with the date of the visit it came from -
// only ever returned by the patient-level aggregate feed below.
export interface PatientVisitNoteEntryResponse extends VisitNoteEntryResponse {
  visit_date: string;
}

// ── API ────────────────────────────────────────────────────────────────────────

export const NotesAPI = {
  list(visitId: string): Promise<VisitNoteEntryResponse[]> {
    return apiRequest<VisitNoteEntryResponse[]>(ROUTES.VISIT_NOTES(visitId));
  },

  create(visitId: string, data: VisitNoteEntryCreate): Promise<VisitNoteEntryResponse> {
    return apiRequest<VisitNoteEntryResponse>(ROUTES.VISIT_NOTES(visitId), {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  update(entryId: string, data: VisitNoteEntryUpdate): Promise<VisitNoteEntryResponse> {
    return apiRequest<VisitNoteEntryResponse>(ROUTES.NOTE(entryId), {
      method: "PUT",
      body: JSON.stringify(data),
    });
  },

  history(entryId: string): Promise<VisitNoteEntryVersionResponse[]> {
    return apiRequest<VisitNoteEntryVersionResponse[]>(ROUTES.NOTE_VERSIONS(entryId));
  },

  // Single request, server-side join across every one of the patient's
  // visits - replaces the old client-side Promise.all(visits.map(...)) fan-out
  // in PatientProfilePage.tsx (see PR #234 review: that fan-out could saturate
  // the Lambda DB connection pool for patients with many visits).
  listForPatient(patientId: string): Promise<PatientVisitNoteEntryResponse[]> {
    return apiRequest<PatientVisitNoteEntryResponse[]>(ROUTES.PATIENT_VISIT_NOTES(patientId));
  },
};
